import type { StaffRouter } from '@eklevo/api/staff-trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import type { inferRouterOutputs } from '@trpc/server'
import { useCallback, useEffect } from 'react'
import { onSessionEnd } from '../../lib/session-end'
import { endStaffSession, takeEndReason } from './session-end'
import { useTRPC } from './trpc'

export type StaffState = inferRouterOutputs<StaffRouter>['auth']['state']
export type SignedInState = Extract<StaffState, { status: 'signed-in' }>

/**
 * Where the staff session stands: none, the email link done and the passkey to come, or signed
 * in. Asked again whenever the tab is shown; the server does not count it as activity.
 */
export function useStaffState() {
  const trpc = useTRPC()
  return useQuery({
    ...trpc.auth.state.queryOptions(),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

/** Signing out ends the session view in every tab of the back office in this browser. */
export function useSignOut() {
  const trpc = useTRPC()
  return useMutation(
    trpc.auth.signOut.mutationOptions({ onSettled: () => endStaffSession('salida') }),
  )
}

/**
 * Mounted once at the root: when the session ends, here or in another tab, this tab forgets
 * everything it holds and shows the sign-in page with the reason.
 */
export function useStaffSessionSync() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const endView = useCallback(() => {
    const motivo = takeEndReason()
    // The sign-in page shows nothing of anyone, and may be half filled in: it stays as it is.
    if (router.state.location.pathname === '/entrar') return
    queryClient.clear()
    void router.navigate({ to: '/entrar', search: { motivo }, replace: true, ignoreBlocker: true })
  }, [queryClient, router])
  useEffect(() => onSessionEnd(endView), [endView])
}
